import React from "react"

import {CustomReward, CustomRewardTransaction} from "@irati/types"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import adminTracking, {CustomRewardTypes} from "@irati-tracking/admin/admin"
import StatusType from "@irati/wallet/types/enums/Status"
import authentication from "@irati/auth"
import perkboxLogo from "@irati/ui/images/logo-alt.svg"

import SecondaryDetails from "../../SecondaryDetails/ViewMessage"
import {useBranding} from "@irati/brand"
import {StyledExpander} from "../../styled"
import PrimaryDetails from "../../PrimaryDetails"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"

interface Props {
  item: CustomRewardTransaction
  isUser?: boolean
  className?: string
  isRedeemable?: boolean
  handleShowRedemptionDetails: (customReward: CustomReward) => void
  handleRedeemCustomReward: (transaction: CustomRewardTransaction) => void
}

const getTransactionItemStatus = (item: CustomRewardTransaction, isRedeemable?: boolean, isUser?: boolean) => {
  if (isUser) {
    return item.isUsed ? StatusType.REDEEMED : StatusType.RECEIVED
  }
  if (!isRedeemable) {
    return item.receiver?.email === authentication.user().email ? StatusType.RECEIVED : StatusType.SENT
  }
  return item.isUsed ? StatusType.USED : StatusType.UNUSED
}

const CustomRewardTransactionItem = (props: Props) => {
  const {item, className, isRedeemable, isUser, handleShowRedemptionDetails, handleRedeemCustomReward} = props
  const branding = useBranding()
  const {formatMessage} = useIntl()
  const sender = item.author
  const messages = {
    messageOne: "platform.statementItem.adminReward.rewarded.received.primaryDetails.title.sender.one",
    messageTwo: "platform.statementItem.adminReward.rewarded.received.primaryDetails.title.receiver",
    messageThird: "platform.statementItem.adminReward.rewarded.received.primaryDetails.title.external"
  }
  const status = getTransactionItemStatus(item, isRedeemable, isUser)
  const title =
    (sender.email !== authentication.user().email && item.receiver.email !== authentication.user().email) || isRedeemable
      ? messages.messageThird
      : status === StatusType.RECEIVED || status === StatusType.REDEEMED
      ? messages.messageTwo
      : messages.messageOne
  const userMessage = formatMessage(
    {id: title},
    {
      sender: firstnameLastname(sender),
      user: firstnameLastname(item.receiver)
    }
  )
  const primaryDetails = {
    image: branding?.businessLogoUrl || perkboxLogo,
    title: userMessage,
    subTitle: formatMessage({id: "platform.btn.viewDetails"}),
    amountTitle: item.customReward.title,
    status
  }
  const secondaryDetails = {
    userMessage,
    message: item.recognition.message
  }
  const redeemBtnTranslation = !item.isUsed ? "admin.customRewards.btn.markAsUsed" : "admin.customRewards.btn.markedAsUsed"
  const showRedemptionDetails = () => handleShowRedemptionDetails(item.customReward)
  const onReedem = () => {
    handleRedeemCustomReward(item)
    adminTracking.sendCustomRewardEvent(CustomRewardTypes.MARK_CUSTOM_REWARD)
  }
  return (
    <StyledExpander
      className={className}
      PrimaryElement={() => <PrimaryDetails imgHeightAuto data={primaryDetails} />}
      SecondaryElement={
        <SecondaryDetails data={secondaryDetails}>
          {isRedeemable && (
            <Flex marginTop={2} width="100%">
              <Button full primary={!item.isUsed} disabled={item.isUsed} onClick={() => onReedem()}>
                <FormattedMessage id={redeemBtnTranslation} />
              </Button>
            </Flex>
          )}
          {isUser && (
            <Flex marginTop={2} width="100%">
              <Button full primary onClick={showRedemptionDetails}>
                <FormattedMessage id="wallet.customRewards.btn.viewRedemptionDetails" />
              </Button>
            </Flex>
          )}
        </SecondaryDetails>
      }
    />
  )
}

export default CustomRewardTransactionItem
